import { InboxOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Row,
  Spin,
  Upload,
} from 'antd';
import { getHoraDelServidor } from 'api/configuracion';
import { getCreditos } from 'api/pae/creditos';
import { getEjecutores } from 'api/pae/ejecutores';
import { getEstadosDeEjecucion } from 'api/pae/estadosDeEjecucion';
import { postArchivosDeEstadosDeEjecucion, postSiguienteEstatusDeEjecucion } from 'api/pae/estatusDeEjecuciones';
import { getMotivosDeCancelacion } from 'api/pae/motivosDeCancelacion';
import FormSubmitControls from 'components/FormSubmitControls';
import { InputSearchPro } from 'components/InputSearch';
import Select from 'components/Select';
import PropTypes from 'prop-types';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { onError } from 'utils/handlers';
import {
  getEjecucionPermissionsInfo,
  getIndividualAlertMessage,
  getMultipleAlertMessage,
  // getMultipleAlertMessage,
} from 'views/PAE/ConsultaDeEjecuciones/utils/ejecucionPermissions';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';
import { DATE_FORMAT } from 'utils/formatValues';
import BusquedaAvanzadaDeCredito from './BusquedaAvanzadaDeCreditos';

const { Dragger } = Upload;

const ModalEstatusEjecucion = ({
  visible,
  setVisible,
  callback,
  estatusDeEjecucion,
  nextStatus,
  currentStatus,
  ejecuciones,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [motivosDeCancelacion, setMotivosDeCancelacion] = useState([]);
  const [ejecutores, setEjecutores] = useState([]);
  const [creditoFiscal, setCreditoFiscal] = useState([]);
  const [visibleBusquedaDeCredito, setVisibleBusquedaDeCredito] = useState(false);

  // Cache para evitar llamadas repetidas mientras el componente esté montado
  const ejecutoresCacheRef = useRef(null);
  const motivosCacheRef = useRef(null);
  const ultimoEstatusSolicitadoRef = useRef(null);

  const selectedEstatus = Form.useWatch('estado_de_ejecucion', form);

  // Map para búsquedas O(1) y colecciones prefiltradas
  const estatusMap = useMemo(() => {
    const map = new Map();
    estatusDeEjecucion.forEach((e) => {
      map.set(e.id, e);
    });
    return map;
  }, [estatusDeEjecucion]);

  const estatusOpcionesSelect = useMemo(
    () => estatusDeEjecucion.filter((e) => {
      const curr = estatusMap.get(currentStatus);

      return e.puede_aplicar
      && !e.es_estatus_unico_inicial
      && curr?.estatus_de_ejecucion_siguientes.includes(e.id);
    }),
    [estatusDeEjecucion, currentStatus],
  );

  const estatusDeEjecucionSeleccionado = useMemo(() => {
    const item = estatusMap.get(selectedEstatus);
    return item && item.puede_aplicar ? item : null;
  }, [estatusMap, selectedEstatus, estatusDeEjecucion]);

  // Lógica centralizada para permisos de ejecución usando utilidades
  const {
    ejecucionesToApply,
    shouldShowIndividualAlert,
    shouldShowMultipleAlert,
    isIndividualProcess,
  } = useMemo(() => getEjecucionPermissionsInfo(
    creditoFiscal,
    estatusDeEjecucionSeleccionado,
    ejecuciones,
  ),
  [creditoFiscal, estatusDeEjecucionSeleccionado, ejecuciones]);

  // Mensajes de alerta generados dinámicamente
  const individualAlertMessage = useMemo(() => getIndividualAlertMessage(
    shouldShowIndividualAlert,
    estatusDeEjecucionSeleccionado?.descripcion,
  ),
  [
    shouldShowIndividualAlert,
    ejecucionesToApply,
    estatusDeEjecucionSeleccionado?.descripcion,
  ]);

  const multipleAlertMessage = useMemo(() => getMultipleAlertMessage(
    shouldShowMultipleAlert,
    creditoFiscal?.[0]?.clave,
  ),
  [
    shouldShowMultipleAlert,
    creditoFiscal?.[0]?.clave,
  ]);

  const resetAndValidate = () => {
    form.resetFields(['clave', 'credito_fiscal']);
    setCreditoFiscal([]);
  };

  const onChangeEstatus = async (value) => {
    if (!value) return;

    ultimoEstatusSolicitadoRef.current = value;
    const status = estatusMap.get(value);
    if (!status) return;
    setLoading(true);

    if (!status.asigna_credito_fiscal) {
      resetAndValidate();
    }

    // Limpiar campos y datos si dejan de aplicar
    if (!status.asigna_ejecutor_iniciando_estado) {
      setEjecutores([]);
      form.setFieldsValue({
        ejecutor: undefined,
        fecha_de_asigno_ejecutor: undefined,
      });
    }
    if (!status.es_estatus_unico_de_cancelacion_de_ejecucion) {
      setMotivosDeCancelacion([]);
      form.setFieldsValue({
        motivo_de_cancelacion: undefined,
        observacion_de_cancelacion: undefined,
      });
    }

    // Cargar datos requeridos con cache y control de concurrencia
    if (status.asigna_ejecutor_iniciando_estado) {
      if (Array.isArray(ejecutoresCacheRef.current)) {
        if (ultimoEstatusSolicitadoRef.current === value) {
          setEjecutores(ejecutoresCacheRef.current);
        }
      } else {
        const res = await getEjecutores();
        ejecutoresCacheRef.current = (res || []).filter((e) => e.estados_globales !== 5);
        if (ultimoEstatusSolicitadoRef.current === value) {
          setEjecutores(ejecutoresCacheRef.current);
        }
      }
    }

    if (status.es_estatus_unico_de_cancelacion_de_ejecucion) {
      if (Array.isArray(motivosCacheRef.current)) {
        if (ultimoEstatusSolicitadoRef.current === value) {
          setMotivosDeCancelacion(motivosCacheRef.current);
        }
      } else {
        const res = await getMotivosDeCancelacion();
        motivosCacheRef.current = res || [];
        if (ultimoEstatusSolicitadoRef.current === value) {
          setMotivosDeCancelacion(motivosCacheRef.current);
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    const preselectIfNeeded = async () => {
      // si el modal no está visible, no hacer nada
      if (!visible) return;

      // si no hay estatus actual, no hacer nada
      if (!nextStatus) {
        setLoading(false);
        return;
      }

      // buscar el estatus actual en la lista de opciones
      let estatusActual = estatusOpcionesSelect.find((e) => e.id === nextStatus);

      // buscar los estatus que pueden ser siguientes al actual
      const posiblesSiguientes = estatusDeEjecucion.filter((e) => {
        const siguiente = estatusMap.get(currentStatus);
        return siguiente?.estatus_de_ejecucion_siguientes.includes(e.id)
        && e.puede_aplicar;
      });

      // si no se encontró el estatus actual, usar el primer estatus posible
      if (!estatusActual) {
        [estatusActual] = posiblesSiguientes;
      }

      // si no se encontró el estatus actual, o no puede aplicarse, no hacer nada
      if (!estatusActual || !estatusActual.puede_aplicar) {
        setLoading(false);
        return;
      }

      // seleccionar el estatus actual
      form.setFieldsValue({ estado_de_ejecucion: estatusActual.id });
      await onChangeEstatus(estatusActual.id);

      setLoading(false);
    };

    preselectIfNeeded();
  }, [nextStatus, estatusMap, visible]);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    form.resetFields(['clave', 'credito_fiscal']);
    setCreditoFiscal([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (isIndividualProcess && ejecuciones.length > 1) {
        message.warning('El estatus seleccionado solo puede aplicarse a una reporte moroso. Por favor, seleccione un reporte moroso y vuelva a intentar.');
        return;
      }

      if (estatusDeEjecucionSeleccionado?.asigna_credito_fiscal && !creditoFiscal?.length) {
        message.warning('No se ha seleccionado un crédito fiscal. Por favor, seleccione un crédito fiscal y vuelva a intentar.');
        return;
      }

      if (!creditoFiscal?.[0]?.permitir_relacion_a_multiples_ejecuciones
        && ejecuciones.length > 1 && estatusDeEjecucionSeleccionado?.asigna_credito_fiscal) {
        message.warning('El crédito fiscal seleccionado no permite procesar múltiples ejecuciones simultáneamente. Debe seleccionar un crédito fiscal que tenga habilitada esta funcionalidad.');
        return;
      }

      if (estatusDeEjecucionSeleccionado?.es_estatus_unico_de_cancelacion_de_ejecucion) {
        const horaDelServidor = await getHoraDelServidor();
        values.fecha_de_cancelacion = horaDelServidor?.format(DATE_FORMAT);
      }

      const response = await postSiguienteEstatusDeEjecucion({
        ...values,
        ejecuciones: ejecucionesToApply,
      });
      if (response) {
        // si el estatus actual requiere adjuntar documentos, se debe adjuntar el documento
        if (estatusDeEjecucionSeleccionado?.adjunta_documentos
            && values.documentos?.fileList?.length > 0) {
          try {
            // se obtienen los estados de ejecución de la ejecución
            const estadosDeEjecucion = await getEstadosDeEjecucion({
              ejecucion: ejecuciones[0],
            });
              // se obtiene el último estado de ejecución
            const lastEstadoDeEjecucion = estadosDeEjecucion.at(-1);

            if (lastEstadoDeEjecucion) {
              // se adjuntan los documentos al ultimo estado de ejecución
              const validFiles = values.documentos.fileList.filter(
                (file) => file.originFileObj || file,
              );

              const promises = validFiles.map((file) => {
                const fileToSend = file.originFileObj || file;
                return postArchivosDeEstadosDeEjecucion({
                  estado_de_ejecucion_pae: lastEstadoDeEjecucion.id,
                  documento: fileToSend,
                });
              });

              if (promises.length > 0) {
                await Promise.all(promises);
                message.success('Documentos adjuntados correctamente');
              }
            }
          } catch (error) {
            message.error(
              'Error al adjuntar documentos, pero el cambio de estatus se realizó correctamente',
            );
          }
        }
        await callback(response);
        onCancel();
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const rules = {
    required: [
      {
        required: true,
      },
    ],
  };

  const handleSearchCreditoFiscal = async () => {
    try {
      const { clave } = await form.validateFields(['clave']);
      if (!clave) return;
      setLoading(true);
      const response = await getCreditos({
        clave,
        origen_del_credito: estatusDeEjecucionSeleccionado?.origen_del_credito,
      });

      const creditos = response
        .filter((c) => c.estados_globales !== ESTADOS_GLOBALES_MAP.CANCELADO);

      if (!creditos.length) {
        message.warn('No se encontró ningún crédito fiscal.');
        setCreditoFiscal([]);
        form.setFieldsValue({
          credito_fiscal: null,
        });
        return;
      }

      setCreditoFiscal([{
        ...creditos[0],
      }]);
      form.setFieldsValue({
        credito_fiscal: creditos[0].id,
      });
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const extensions = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
  const accept = extensions.map((e) => `.${e}`).join(',');

  const beforeUpload = (file, fileList) => {
    const ext = file.name.split('.').pop();
    const isValidExt = extensions.includes(ext);
    const isLt30M = file.size / 1024 / 1024 < 30;

    if (!isValidExt) {
      message.warn('Solo se admiten archivos en formato PDF, JPG, JPEG, PNG, DOC, DOCX, XLS, XLSX y CSV');
      return false;
    }

    if (!isLt30M) {
      message.warn('El archivo no debe sobrepasar los 30MB');
      return false;
    }

    // Agregar archivos válidos al formulario con el archivo original
    const currentFileList = form.getFieldValue('documentos')?.fileList || [];
    const filesWithOrigin = fileList.map((f) => ({
      uid: f.uid || Math.random().toString(36).substr(2, 9),
      name: f.name,
      status: 'done',
      originFileObj: f, // Guardar el archivo original
    }));
    const newFileList = [...currentFileList, ...filesWithOrigin];
    form.setFieldsValue({ documentos: { fileList: newFileList } });

    message.success(
      `${fileList.length} archivo${fileList.length > 1 ? 's' : ''} agregado${fileList.length > 1 ? 's' : ''} correctamente`,
    );
    return false;
  };

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      centered
      destroyOnClose
      title={(
        <FormSubmitControls
          label="Actualizar estatus"
          onFinish={onFinish}
          onCancel={onCancel}
          loading={loading}
        />
      )}
    >
      <Spin spinning={loading} tip="Cargando...">
        {/* Alertas de información sobre el comportamiento de las ejecuciones */}
        {shouldShowIndividualAlert && (
          <Alert
            message={individualAlertMessage}
            type="error"
            showIcon
            style={{ marginBottom: 10 }}
          />
        )}
        {shouldShowMultipleAlert && (
          <Alert
            message={multipleAlertMessage}
            type="success"
            showIcon
            style={{ marginBottom: 10 }}
          />
        )}
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={10}>
            {estatusDeEjecucionSeleccionado?.asigna_credito_fiscal && (
            <InputSearchPro
              rules={rules.required}
              tooltip="Búsqueda de crédito fiscal por clave"
              name="clave"
              label="Crédito fiscal"
              inputProps={{
                onPressEnter: () => handleSearchCreditoFiscal(),
              }}
              onClickAdvanced={() => setVisibleBusquedaDeCredito(true)}
              breakPoints={{
                xs: 24,
                sm: 24,
                md: 24,
              }}
              resultProps={{
                breakPoints: {
                  xs: 24,
                  sm: 24,
                  md: 24,
                },
                name: 'credito_fiscal',
                render: (value) => `${value.clave} - ${value?.numero_de_cuenta_del_credito || 'Sin número de cuenta'}`,
                label: 'Crédito fiscal',
                dataSource: creditoFiscal,
                onClear: resetAndValidate,
              }}
            />
            )}
            <Col span={24}>
              <Form.Item
                name="estado_de_ejecucion"
                label="Estatus"
                rules={rules.required}
              >
                <Select
                  dataSource={estatusOpcionesSelect}
                  onChange={onChangeEstatus}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="fecha_de_actuacion"
                label="Fecha de actuación"
                rules={rules.required}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  allowClear
                />
              </Form.Item>
            </Col>
            {estatusDeEjecucionSeleccionado?.es_estatus_unico_de_cancelacion_de_ejecucion && (
              <>
                <Col span={24}>
                  <Form.Item
                    name="motivo_de_cancelacion"
                    label="Motivo de cancelación"
                    rules={rules.required}
                  >
                    <Select dataSource={motivosDeCancelacion} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="observacion_de_cancelacion"
                    label="Observaciones de cancelación"
                    rules={rules.required}
                  >
                    <Input.TextArea autoSize={{ minRows: 3, maxRows: 5 }} allowClear />
                  </Form.Item>
                </Col>
              </>
            )}
            {estatusDeEjecucionSeleccionado?.asigna_ejecutor_iniciando_estado && (
            <>
              <Col span={24}>
                <Form.Item
                  name="ejecutor"
                  label="Ejecutor"
                  rules={rules.required}
                >
                  <Select
                    dataSource={ejecutores}
                    render={(item) => item.nombre_completo}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="fecha_de_asigno_ejecutor"
                  label="Fecha de asignación de ejecutor"
                  rules={rules.required}
                >
                  <DatePicker
                    format="DD/MM/YYYY"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </>
            )}
            {estatusDeEjecucionSeleccionado?.adjunta_documentos && (
            <Col span={24}>
              <Form.Item
                name="documentos"
                label="Documentos adjuntos"
              >
                <Dragger
                  className="custom-dragger"
                  beforeUpload={beforeUpload}
                  accept={accept}
                  multiple
                  onRemove={(file) => {
                    const currentFileList = form.getFieldValue('documentos')?.fileList || [];
                    const newFileList = currentFileList.filter((f) => f.uid !== file.uid);
                    form.setFieldsValue({ documentos: { fileList: newFileList } });
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Haga click o arrastre los documentos en esta área.
                  </p>
                  <p className="ant-upload-hint">
                    Soporte para subir varios archivos en forma to PDF, JPG, JPEG, PNG, DOC,
                    DOCX, XLS, XLSX, CSV (máximo 30MB por archivo).
                  </p>
                </Dragger>
              </Form.Item>
            </Col>
            )}
          </Row>
          <Form.Item hidden>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
      </Spin>
      <BusquedaAvanzadaDeCredito
        visible={visibleBusquedaDeCredito}
        setVisible={setVisibleBusquedaDeCredito}
        origenDeCredito={estatusDeEjecucionSeleccionado?.origen_del_credito}
        callback={(credito) => {
          setCreditoFiscal([credito]);
          form.setFieldsValue({
            credito_fiscal: credito.id,
          });
        }}
      />
    </Modal>
  );
};

ModalEstatusEjecucion.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  callback: PropTypes.func.isRequired,
  estatusDeEjecucion: PropTypes.arrayOf(PropTypes.shape({
    puede_aplicar: PropTypes.bool.isRequired,
  })).isRequired,
  nextStatus: PropTypes.number,
  currentStatus: PropTypes.number,
  ejecuciones: PropTypes.arrayOf(PropTypes.number).isRequired,
};

ModalEstatusEjecucion.defaultProps = {
  nextStatus: null,
  currentStatus: null,
};

export default ModalEstatusEjecucion;
